import { FC, useCallback, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Button } from '@mui/material';

import { updateUserProfile } from '@api/profile';
import Modal from '@components/Modal/Modal';
import { IUserProfileNormalFormValues } from '@interfaces/profile';
import { handleGetUserProfile } from '@store/features/auth/user/actions';
import { userDataSelector } from '@store/features/auth/user/selectors';
import useDispatch from '@store/hooks/useDispatch';
import handleToast from '@utils/handleToast';

import UnsavedChangesModal from './UnsavedChangesModal/UnsavedChangesModal';
import UserProfileNormalForm from './UserProfileNormalForm/UserProfileNormalForm';
import { IUserProfileModalNormalProps } from './UserProfileModalNormal.interface';

import './style.scss';

const handleChangePassword = (): void => {
  if (process.env.REACT_APP_FORGOT_PASS_URL) {
    window.location.href = process.env.REACT_APP_FORGOT_PASS_URL;
  }
};

const UserProfileModalNormal: FC<IUserProfileModalNormalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const user = useSelector(userDataSelector);
  const initialValues = useMemo(
    () => ({
      genderId: user?.athleteGenderId,
      roleId: user?.athleteRoleId,
      sportIds: user?.athleteSportIds,
    }),
    [user],
  );

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUnsavedChangesModalShown, setIsUnsavedChangesModalShown] = useState(false);

  const handleClose = useCallback(() => {
    isFormDirty ? setIsUnsavedChangesModalShown(true) : onClose();
  }, [isFormDirty, onClose]);

  const handleUnsavedChangesModalDiscard = useCallback(() => {
    onClose();
    setIsUnsavedChangesModalShown(false);
  }, [onClose]);

  const handleUnsavedChangesModalContinue = useCallback(() => {
    setIsUnsavedChangesModalShown(false);
  }, []);

  const handleValidateForm = useCallback(
    (values: IUserProfileNormalFormValues) => {
      const errors: Partial<Record<keyof IUserProfileNormalFormValues, string | boolean>> = {};

      if (user?.showRole && !values.roleId) errors.roleId = true;

      return errors;
    },
    [user],
  );

  const handleSubmitForm = useCallback(
    async (values: IUserProfileNormalFormValues): Promise<void> => {
      setIsSubmitting(true);

      try {
        const response = await updateUserProfile({
          athleteGenderId: values.genderId || null,
          athleteRoleId: values.roleId || null,
          athleteSportIds: values.sportIds,
        });

        if (response.status === 200) {
          handleToast('success', t('common.messages.profileUpdatedSuccessfully'));
          dispatch(handleGetUserProfile());
          onClose();
        }
      } catch (_) {
        setIsSubmitting(false);
      }
    },
    [dispatch, t, onClose],
  );

  return (
    <>
      <Modal
        title={t('userProfile.title')}
        open={open}
        onClose={handleClose}
        rightActionTitle={t('common.save')}
        isRightActionLoading={isSubmitting}
        onLeftAction={handleClose}
        leftActionTitle={t('common.cancel')}
        className="user-profile-modal-normal"
        submitFormId="user-profile-normal-form"
      >
        <Box className="user-profile-modal-normal__wrapper-info">
          <Box className="user-profile-modal-normal__email">
            <Box>{user?.email}</Box>
            <Button variant="text" onClick={handleChangePassword}>
              {t('userProfile.changePassword')}
            </Button>
          </Box>

          <Box className="user-profile-modal-normal__info">{t('userProfile.normalInfo')}</Box>

          {user?.organizationName && (
            <Box className="user-profile-modal-normal__description">
              {t('userProfile.normalDescription')} {user.organizationName}{' '}
              {t('userProfile.organization')}.
            </Box>
          )}
        </Box>

        <Form
          // @ts-ignore because of custom setIsFormDirty prop
          component={UserProfileNormalForm}
          setIsFormDirty={setIsFormDirty}
          initialValues={initialValues}
          validate={handleValidateForm}
          onSubmit={handleSubmitForm}
        />
      </Modal>

      <UnsavedChangesModal
        open={isUnsavedChangesModalShown}
        onDiscard={handleUnsavedChangesModalDiscard}
        onContinue={handleUnsavedChangesModalContinue}
      />
    </>
  );
};

export default UserProfileModalNormal;
